import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, ArrowLeft, FileText, CheckCircle, AlertTriangle, Scale, CreditCard, MessageSquare, Shield } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const homeRoute = isAuthenticated ? '/dashboard' : '/';
  const lastUpdated = "May 29, 2026";
  const contactEmail = "support@virtualfit.work";

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate(homeRoute)}
            className="flex items-center space-x-2 text-gray-700 hover:text-rose-500 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back to Home</span>
          </button>
          
          <button
            onClick={() => navigate(homeRoute)}
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <Sparkles className="w-7 h-7 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
              VirtualFit
            </span>
          </button>
          
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-400 to-orange-400 rounded-full mb-6">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4" data-testid="terms-title">Terms of Service</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <CheckCircle className="w-6 h-6 mr-3 text-rose-500" />
                Agreement to Terms
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Welcome to VirtualFit. These Terms of Service ("Terms") govern your access to and use of 
                the VirtualFit website, application, and services (collectively, the "Services"). The Services 
                are owned and operated by Aarti Shah, operating as a sole proprietorship doing business as 
                VirtualFit ("VirtualFit", "we", "us", or "our"). By accessing or using our Services, you agree 
                to be bound by these Terms.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                If you do not agree to these Terms, you may not access or use our Services. We may update 
                these Terms from time to time, and your continued use of the Services after any changes 
                constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Eligibility */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <CheckCircle className="w-6 h-6 mr-3 text-rose-500" />
                Eligibility
              </h2>
              <p className="text-gray-600 leading-relaxed">
                You must be at least 18 years old to use our Services. By using our Services, you represent 
                and warrant that you meet this age requirement and have the legal capacity to enter into 
                these Terms.
              </p>
            </section>

            {/* Account Terms */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <CheckCircle className="w-6 h-6 mr-3 text-rose-500" />
                Account Registration
              </h2>
              <p className="text-gray-600 leading-relaxed">
                To access certain features of our Services, you must create an account. When creating an account, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Notify us immediately of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>

            {/* Subscription Plans */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <CreditCard className="w-6 h-6 mr-3 text-rose-500" />
                Subscription Plans & Payments
              </h2>
              <p className="text-gray-600 leading-relaxed">
                VirtualFit offers various subscription plans for Brands, Influencers, and Personal users. 
                By subscribing to a paid plan, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Pay all applicable fees based on your selected plan</li>
                <li>Provide valid payment information</li>
                <li>Accept automatic renewal of your subscription unless cancelled</li>
                <li>Understand that fees are generally non-refundable once the billing period has begun, except as stated below or as required by applicable law</li>
              </ul>
              
              <div className="mt-4 p-4 bg-rose-50 rounded-xl border border-rose-200">
                <p className="text-gray-700 text-sm">
                  Due to the immediate infrastructure costs, server-side processing power, and digital nature of 
                  generative AI image creation, all fees allocated to virtual try-on generations or subscription 
                  cycles are consumed immediately upon processing. All sales are final, and no refunds or pro-rated 
                  credits will be issued for partially used billing cycles or unused image credits, subject to 
                  mandatory local consumer laws.
                </p>
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-gray-700 text-sm">
                  <strong>EU/EEA Right of Withdrawal:</strong> If you are a consumer in the EU/EEA, you have a 14-day 
                  right of withdrawal from the date of purchase under the Consumer Rights Directive (2011/83/EU). However, 
                  by using the virtual try-on service during the withdrawal period, you expressly consent to the 
                  performance of the digital content service beginning immediately, and you acknowledge that you will lose 
                  your right of withdrawal once the service has been fully performed. If you have not used any try-on 
                  credits during the 14-day period, you are entitled to a full refund. See our{' '}
                  <a href="/refund-policy" className="text-rose-500 hover:underline">Refund Policy</a> for details.
                </p>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Free Trial</h3>
              <p className="text-gray-600 leading-relaxed">
                We offer a limited free trial of 2 virtual try-on images to unregistered users. This trial 
                is intended for personal evaluation only and may be subject to usage limits tracked by 
                browser identification.
              </p>
              <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-gray-700 text-sm">
                  <strong>Consent Requirement:</strong> Before using the free trial, you will be presented with a 
                  mandatory biometric consent checkbox. By checking this box and uploading a photo, you provide 
                  explicit, informed consent for VirtualFit to process your biometric data (facial geometry and 
                  body measurements) solely for the purpose of generating your virtual try-on image, as described 
                  in our{' '}
                  <a href="/privacy" className="text-rose-500 hover:underline">Privacy Policy</a> and{' '}
                  <a href="/biometric-policy" className="text-rose-500 hover:underline">Biometric Data Policy</a>. 
                  Your photo is processed in real-time and is not stored beyond the session unless you create an 
                  account. You may withdraw consent at any time by closing the page, which will immediately halt 
                  any processing. You cannot proceed with photo upload without checking the consent box.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Cancellation</h3>
              <p className="text-gray-600 leading-relaxed">
                You may cancel your subscription at any time through your account settings. Upon cancellation, 
                you will continue to have access to your plan until the end of your current billing period.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-rose-500" />
                Acceptable Use Policy
              </h2>
              <p className="text-gray-600 leading-relaxed">
                When using our Services, you agree NOT to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Upload or share content that is illegal, harmful, or violates others' rights</li>
                <li>Upload images containing nudity, violence, or inappropriate content</li>
                <li>Impersonate any person or entity</li>
                <li>Use the Services for any fraudulent or deceptive purpose</li>
                <li>Attempt to bypass security measures or access unauthorized areas</li>
                <li>Interfere with the proper functioning of the Services</li>
                <li>Use automated systems to access the Services without permission</li>
                <li>Resell or redistribute the Services without authorization</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-rose-500" />
                Intellectual Property Rights
              </h2>
              <p className="text-gray-600 leading-relaxed">
                The Services, including all content, features, and functionality, are owned by VirtualFit 
                and are protected by intellectual property laws. You retain ownership of the images you upload, 
                but grant us a limited license to process them for providing the virtual try-on service.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Your Content</h3>
              <p className="text-gray-600 leading-relaxed">
                By uploading content to our Services, you represent that you have the necessary rights to 
                share such content and grant us permission to use it for providing the Services. You are 
                solely responsible for the content you upload.
              </p>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Brand Portfolios</h3>
              <p className="text-gray-600 leading-relaxed">
                Brand users are responsible for ensuring they have the right to display and distribute 
                all clothing images and product information uploaded to their portfolios.
              </p>
            </section>

            {/* Collaborations */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <MessageSquare className="w-6 h-6 mr-3 text-rose-500" />
                Brand-Influencer Campaigns
              </h2>
              <p className="text-gray-600 leading-relaxed">
                VirtualFit facilitates paid collaboration campaigns between Brands and Influencers. Key terms:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li><strong>Campaign Payments:</strong> Brands fund campaigns through VirtualFit. Payments are held in escrow and released to Influencers within five (5) business days of the Brand approving the submitted content.</li>
                <li><strong>Commission:</strong> VirtualFit charges a platform fee on campaign transactions, which is deducted before payout.</li>
                <li><strong>Content Approval:</strong> Brands have fourteen (14) calendar days from content submission to review and approve or reject deliverables. If no action is taken within this period, the content is deemed approved and payment is automatically released.</li>
                <li><strong>Disputes:</strong> VirtualFit may mediate disputes but is not liable for agreements between users. If a dispute arises, payments will be held in escrow until resolved.</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Users are expected to communicate professionally and honor their commitments. VirtualFit 
                reserves the right to terminate accounts that engage in abusive or fraudulent behavior.
              </p>
            </section>

            {/* AI-Generated Content */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-rose-500" />
                AI-Generated Content & Watermarking
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Our AI-powered virtual try-on service generates images using artificial intelligence. By using this service:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>You acknowledge that generated images are AI-created approximations</li>
                <li>AI-generated images are automatically watermarked with "VirtualFit" for copyright protection</li>
                <li>You may download and use generated images for personal or commercial purposes as permitted by your plan</li>
                <li>Watermarks may not be removed, altered, obscured, or cropped from generated content</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Watermark Removal Policy</h3>
              <p className="text-gray-600 leading-relaxed">
                Intentionally removing, altering, or obscuring the VirtualFit watermark from any AI-generated image 
                constitutes a material breach of these Terms. If we determine that a user has removed or tampered 
                with a watermark, we reserve the right to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Immediately suspend or terminate your account without refund</li>
                <li>Revoke your license to use any AI-generated content from our platform</li>
                <li>Pursue legal action for copyright infringement and breach of contract</li>
                <li>Seek damages including but not limited to statutory copyright damages</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                Watermarked images shared publicly without the watermark may be subject to DMCA takedown requests. 
                Users who repeatedly violate this policy will be permanently banned from the platform.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                Cookies and Consent
              </h2>
              <p className="text-gray-600 leading-relaxed">
                VirtualFit uses cookies and similar technologies. By continuing to use the Services, you acknowledge our use of essential cookies that are required for the platform to function. Non-essential cookies (such as browser fingerprinting for free trial tracking) are only activated after you provide explicit consent through our cookie banner.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li><strong>Essential cookies</strong> (login sessions, security tokens, consent preferences) are always active and do not require consent.</li>
                <li><strong>Non-essential cookies</strong> (browser fingerprinting via FingerprintJS) are only set if you click "Accept All" on the cookie consent banner.</li>
                <li>If you reject non-essential cookies, free trial tracking falls back to IP-based identification, which may be less accurate.</li>
                <li>You can reset your cookie preferences at any time by clearing your browser's local storage for this site.</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                For full details on the cookies we use and your rights, please see our{' '}
                <a href="/privacy" className="text-rose-500 hover:underline">Privacy Policy</a>.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                Third-Party Services
              </h2>
              <p className="text-gray-600 leading-relaxed">
                VirtualFit relies on third-party services to provide our platform. By using VirtualFit, you acknowledge and agree to the following:
              </p>
              
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Stripe (Payment Processing)</h4>
                  <p className="text-gray-600 text-sm">
                    All payments are processed through Stripe. VirtualFit is not liable for payment processing errors, 
                    delays, or service outages caused by Stripe. Your use of payment services is subject to 
                    <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline ml-1">Stripe's Terms of Service</a>.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Google Gemini (AI Image Generation)</h4>
                  <p className="text-gray-600 text-sm">
                    Virtual try-on images are generated using Google's Gemini AI technology. Results may vary based on 
                    input quality and AI limitations. VirtualFit does not guarantee specific outcomes and is not liable 
                    for AI-generated content accuracy. Generated images are approximations and should not be relied upon 
                    for exact product representation.
                  </p>
                  <div className="mt-3 p-3 bg-rose-50 rounded-lg border border-rose-200">
                    <p className="text-gray-700 text-sm">
                      <strong>Biometric Data Processing:</strong> Google Gemini processes user-uploaded photos 
                      (which may contain facial geometry and body measurements constituting biometric data) to generate 
                      virtual try-on images. Photos are transmitted to Google Gemini API via encrypted API calls solely 
                      to generate your virtual try-on image. Processing is governed by Google's API Terms of Service 
                      and applicable Data Processing Addendum. We do not knowingly permit Google to use your photos 
                      for model training or any purpose beyond fulfilling the API request. This processing is also 
                      disclosed in our{' '}
                      <a href="/privacy" className="text-rose-500 hover:underline">Privacy Policy</a> and{' '}
                      <a href="/biometric-policy" className="text-rose-500 hover:underline">Biometric Data Policy</a>.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Cloudinary (Image Storage & Delivery)</h4>
                  <p className="text-gray-600 text-sm">
                    User-uploaded images and generated content are stored on Cloudinary's cloud infrastructure. 
                    VirtualFit is not liable for data loss, service interruptions, or security breaches originating 
                    from Cloudinary's services. Your use is subject to 
                    <a href="https://cloudinary.com/tos" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:underline ml-1">Cloudinary's Terms of Service</a>.
                  </p>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-gray-700 text-sm">
                      <strong>Data Processing:</strong> We have executed a Data Processing Agreement (DPA) with 
                      Cloudinary Ltd. in accordance with GDPR Article 28. Cloudinary processes and stores user images 
                      solely on our behalf and is contractually obligated to implement appropriate technical and 
                      organizational security measures.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Resend (Email Communications)</h4>
                  <p className="text-gray-600 text-sm">
                    Transactional emails (account verification, notifications, etc.) are sent through Resend. 
                    VirtualFit is not liable for email delivery failures or delays caused by Resend or recipient 
                    email providers.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">FingerprintJS (Browser Identification)</h4>
                  <p className="text-gray-600 text-sm">
                    We use FingerprintJS for browser fingerprinting to manage free trial access and prevent abuse. 
                    This technology collects device characteristics but does not identify you personally.
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>General Third-Party Disclaimer:</strong> VirtualFit is not responsible or liable for the 
                availability, accuracy, reliability, or performance of any third-party services, including but not 
                limited to Stripe, Google, Cloudinary, Resend, and FingerprintJS. VirtualFit shall not be held liable 
                for any loss, damage, or inconvenience resulting from any failure, interruption, error, or security 
                breach originating from a third-party service. We reserve the right to change third-party 
                providers at any time without notice. Any issues with third-party services should be directed to 
                those providers directly.
              </p>
            </section>

            {/* Disclaimers */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-rose-500" />
                Disclaimers
              </h2>
              <p className="text-gray-600 leading-relaxed">
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not guarantee that:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>The Services will be uninterrupted or error-free</li>
                <li>Virtual try-on results will perfectly represent actual clothing fit</li>
                <li>AI-generated images will be 100% accurate</li>
                <li>The Services will meet all your expectations</li>
              </ul>
              <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-gray-700 text-sm">
                  <strong>Commercial Sizing Disclaimer:</strong> Virtual try-on images are generated for conceptual, 
                  informational, and entertainment purposes only. VirtualFit does not guarantee that AI-generated 
                  garments will match the exact sizing, fit, fabric draping, or color matching of real-world retail 
                  clothing. VirtualFit assumes no liability for real-world garment purchases made by users based on 
                  AI-generated images.
                </p>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-gray-700 text-sm">
                  <strong>Notice for EU/EEA Users:</strong> Nothing in these Terms excludes or limits your statutory 
                  rights as a consumer under EU consumer protection laws, including the Consumer Rights Directive 
                  (2011/83/EU) and the Digital Content Directive (2019/770/EU). Where mandatory consumer protections 
                  apply, the disclaimers and limitations in these Terms are limited to the maximum extent permitted 
                  by applicable law. You may have the right to a remedy (repair, replacement, or refund) if digital 
                  content does not conform to what was agreed.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-rose-500" />
                Limitation of Liability
              </h2>
              <p className="text-gray-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VIRTUALFIT, ITS FOUNDER (AARTI SHAH), AND 
                ANY AGENTS OR AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, 
                GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICES.
              </p>
              <div className="mt-4 p-4 bg-rose-50 rounded-xl border border-rose-200">
                <p className="text-gray-700 font-medium">
                  OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS 
                  OR THE SERVICES SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT OF FEES PAID BY YOU TO 
                  VIRTUALFIT DURING THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM, 
                  OR (B) FIFTY U.S. DOLLARS (US $50.00).
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mt-4">
                This limitation of liability applies regardless of the legal theory on which the claim is based, 
                including breach of contract, tort (including negligence), strict liability, or any other basis, 
                even if VirtualFit has been advised of the possibility of such damages.
              </p>
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-gray-700 text-sm">
                  <strong>Notice for EU/EEA Users:</strong> This limitation does not apply to liability that cannot 
                  be excluded under mandatory EU consumer protection laws, including liability for death or personal 
                  injury caused by negligence, fraud, or fraudulent misrepresentation.
                </p>
              </div>
            </section>

            {/* Termination */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3 text-rose-500" />
                Termination
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We reserve the right to suspend or terminate your access to the Services for violations of these 
                Terms, illegal activity, or conduct that harms VirtualFit or other users.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li><strong>Immediate termination:</strong> For serious violations (fraud, illegal content, security breaches, watermark removal, or uploading content that violates our Acceptable Use Policy), we may terminate your account immediately without prior notice.</li>
                <li><strong>Standard termination:</strong> For other violations or inactivity, we will provide at least fourteen (14) days' written notice via email before terminating your account, during which you may remedy the violation.</li>
              </ul>
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-gray-700 text-sm">
                  <strong>Notice for EU/EEA Users:</strong> Except in cases of serious violations as described above, 
                  EU/EEA users will receive at least thirty (30) days' prior written notice before termination, in 
                  accordance with EU consumer protection requirements. During this notice period, you may export your 
                  data and content.
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mt-4">
                You may terminate your account at any time by contacting us or using the account deletion 
                feature in your settings. Upon termination, your data will be deleted in accordance with our{' '}
                <a href="/privacy" className="text-rose-500 hover:underline">Privacy Policy</a>, except where 
                retention is required by law.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-rose-500" />
                Governing Law & Jurisdiction
              </h2>
              <p className="text-gray-600 leading-relaxed">
                These Terms and any disputes arising out of or relating to these Terms or the Services shall be 
                governed by and construed in accordance with the laws of the <strong>State of New Jersey, United States</strong>, 
                without regard to its conflict-of-law principles.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                Any legal action or proceeding not subject to arbitration (as described below) shall be brought 
                exclusively in the state or federal courts located in <strong>Newark, New Jersey</strong>, and you 
                consent to the personal jurisdiction of such courts.
              </p>
              <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-gray-700 text-sm">
                  <strong>Notice for EU/EEA Users:</strong> If you are a consumer residing in the EU/EEA, you may also 
                  bring proceedings in the courts of your country of residence, and nothing in this section limits your 
                  right to do so under mandatory EU consumer protection regulations.
                </p>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Scale className="w-6 h-6 mr-3 text-rose-500" />
                Dispute Resolution & Binding Arbitration
              </h2>
              <p className="text-gray-600 leading-relaxed">
                <strong>PLEASE READ THIS SECTION CAREFULLY — IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</strong>
              </p>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Informal Resolution First</h3>
              <p className="text-gray-600 leading-relaxed">
                Before initiating any formal dispute resolution, you agree to first contact us at{' '}
                <a href={`mailto:${contactEmail}`} className="text-rose-500 hover:underline">{contactEmail}</a>{' '}
                and attempt to resolve the dispute informally for at least sixty (60) days. Most disputes can be 
                resolved without the formality and expense of arbitration.
              </p>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Binding Arbitration</h3>
              <p className="text-gray-600 leading-relaxed">
                If we cannot resolve a dispute informally, <strong>you and VirtualFit agree that any dispute, claim, or 
                controversy arising out of or relating to these Terms or the Services shall be resolved exclusively 
                through binding individual arbitration</strong>, rather than in court, except that either party may bring 
                claims in small claims court if they qualify.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li>Arbitration shall be administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules</li>
                <li>The arbitration shall be conducted in English, and the seat of arbitration shall be Newark, New Jersey (or by phone/video at your election)</li>
                <li>The arbitrator's decision shall be final and binding and may be entered as a judgment in any court of competent jurisdiction</li>
                <li>Each party shall bear its own costs, except that VirtualFit will pay your filing and hearing fees if your claim is under $10,000</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Class Action & Jury Trial Waiver</h3>
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                <p className="text-gray-700 font-medium">
                  YOU AND VIRTUALFIT AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS 
                  INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, 
                  OR REPRESENTATIVE ACTION. You expressly waive any right to participate in a class action lawsuit 
                  or class-wide arbitration. Unless both you and VirtualFit agree, no arbitrator or judge may 
                  consolidate more than one person's claims or otherwise preside over any form of a representative 
                  or class proceeding.
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>JURY TRIAL WAIVER:</strong> YOU AND VIRTUALFIT HEREBY WAIVE ANY CONSTITUTIONAL AND STATUTORY 
                RIGHTS TO SUE IN COURT AND HAVE A TRIAL IN FRONT OF A JUDGE OR A JURY. You and VirtualFit are 
                instead electing that all claims and disputes shall be resolved by arbitration.
              </p>

              <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3">Opt-Out Right</h3>
              <p className="text-gray-600 leading-relaxed">
                You may opt out of this arbitration agreement by sending written notice to{' '}
                <a href={`mailto:${contactEmail}`} className="text-rose-500 hover:underline">{contactEmail}</a>{' '}
                within thirty (30) days of first accepting these Terms. Your notice must include your name, 
                account email, and a clear statement that you wish to opt out of arbitration.
              </p>
            </section>

            {/* California Privacy Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                California Privacy Rights (CCPA/CPRA)
              </h2>
              <p className="text-gray-600 leading-relaxed">
                If you are a California resident, you have specific rights under the California Consumer Privacy Act 
                (CCPA) as amended by the California Privacy Rights Act (CPRA):
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
                <li><strong>Right to Know:</strong> You may request disclosure of the categories and specific pieces of personal information we have collected about you, the sources of collection, the business purpose, and the third parties with whom we share it.</li>
                <li><strong>Right to Delete:</strong> You may request deletion of your personal information, subject to certain legal exceptions.</li>
                <li><strong>Right to Correct:</strong> You may request correction of inaccurate personal information.</li>
                <li><strong>Right to Opt-Out of Sale/Sharing:</strong> VirtualFit does not sell your personal information. We do not share personal information for cross-context behavioral advertising.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                To exercise your California privacy rights, contact us at{' '}
                <a href={`mailto:${contactEmail}`} className="text-rose-500 hover:underline">{contactEmail}</a>.{' '}
                We will verify your identity before processing your request. For full details, see our{' '}
                <a href="/privacy" className="text-rose-500 hover:underline">Privacy Policy</a>.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                <strong>Categories of personal information collected:</strong> Identifiers (name, email), commercial 
                information (subscription plan, payment history), internet activity (browsing, device info), biometric 
                data (facial geometry for try-on), and user-generated content (uploaded photos).
              </p>
            </section>

            {/* DMCA */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-rose-500" />
                DMCA Copyright Policy
              </h2>
              <p className="text-gray-600 leading-relaxed">
                VirtualFit respects intellectual property rights. If you believe content on our platform infringes 
                your copyright, you may submit a DMCA takedown notice to our designated agent:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-700 text-sm">
                  <strong>DMCA Agent:</strong> VirtualFit Legal Team<br />
                  <strong>Email:</strong>{' '}
                  <a href="mailto:dmca@virtualfit.work" className="text-rose-500 hover:underline">dmca@virtualfit.work</a><br />
                  <strong>Address:</strong> VirtualFit Legal (Aarti Shah, Owner), [P.O. Box to be added within 90 days — contact dmca@virtualfit.work for urgent DMCA inquiries]
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed mt-4">
                Your notice must include: (1) identification of the copyrighted work, (2) the infringing material 
                and its location on VirtualFit, (3) your contact information, (4) a statement of good faith belief, 
                (5) a statement under penalty of perjury that your notice is accurate, and (6) your physical or 
                electronic signature.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <MessageSquare className="w-6 h-6 mr-3 text-rose-500" />
                Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-700">
                  <strong>Email:</strong> {contactEmail}
                </p>
                <p className="text-gray-700 mt-2">
                  <strong>Website:</strong> <a href="https://virtualfit.work" className="text-rose-500 hover:underline">virtualfit.work</a>
                </p>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-500 text-sm">
              © 2026 VirtualFit. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
